/** EPCRA 302 listing conditions, not TRI qualifiers or section 370.13 exemptions.
 * Source: 40 CFR part 355 Appendix A. Solid TPQ pairs do not remove Tier II EHS status.
 */
export const EHS_QUALIFIERS = {
  '7647-01-0': { kind: 'gas_only', label: 'Hydrogen chloride: gas only' },
  '7722-84-1': { kind: 'concentration', label: 'Hydrogen peroxide: concentration greater than 52%' },
} as const;
export type EhsQualifierAnswer = { form: 'gas' | 'aqueous_solution' } | { concentrationPercent: number };
/** A reviewer's saved answer, with their supporting source or note. */
export type EhsQualifierReview = { answer: EhsQualifierAnswer; note: string };
export type EhsQualifierResult = {
  label: string;
  kind: 'gas_only' | 'concentration';
  status: 'applies' | 'does_not_apply' | 'needs_review';
  explanation: string;
  /** null when nothing is saved, or the saved answer does not fit this qualifier. */
  review: EhsQualifierReview | null;
};
export type QualifierComposition = {
  casOrCategory: string;
  isEHS: boolean;
  concentration: number;
  concentrationLower: number | null;
  concentrationUpper: number | null;
};
export function qualifierFingerprint(row: QualifierComposition): string {
  return JSON.stringify([row.concentration, row.concentrationLower, row.concentrationUpper]);
}
/** Why a reviewer's qualifier answer contradicts the listing or the product composition, or null if it is acceptable. */
export function ehsQualifierAnswerIssue(row: QualifierComposition, answer: EhsQualifierAnswer): string | null {
  const rule = EHS_QUALIFIERS[row.casOrCategory as keyof typeof EHS_QUALIFIERS];
  if (!rule || (rule.kind === 'gas_only') !== 'form' in answer)
    return 'Choose an answer for this chemical’s listing qualifier.';
  if (
    'form' in answer &&
    answer.form === 'aqueous_solution' &&
    row.concentrationLower != null &&
    row.concentrationLower >= 1
  )
    return 'A 100% hydrogen chloride composition is not an aqueous solution. Correct the product composition first.';
  if ('concentrationPercent' in answer) {
    const value = answer.concentrationPercent / 100;
    if (
      (row.concentrationLower != null && value < row.concentrationLower) ||
      (row.concentrationUpper != null && value > row.concentrationUpper)
    )
      return 'The verified concentration is outside the recorded composition range. Correct the product composition first.';
  }
  return null;
}
/** The listing condition for an EHS-treated row, or null when the row has no EHS treatment for a condition to rule out. */
export function ehsQualifier(row: QualifierComposition, review: EhsQualifierReview | null): EhsQualifierResult | null {
  const rule = EHS_QUALIFIERS[row.casOrCategory as keyof typeof EHS_QUALIFIERS];
  if (!rule || !row.isEHS) return null;
  const answer = review?.answer ?? null;
  if (rule.kind === 'gas_only') {
    const form = answer && 'form' in answer ? answer.form : null;
    return {
      ...rule,
      review: form && review ? { answer: { form }, note: review.note } : null,
      status: form === 'gas' ? 'applies' : form === 'aqueous_solution' ? 'does_not_apply' : 'needs_review',
      explanation:
        form === 'gas'
          ? 'The hydrogen chloride gas listing applies. Its chemical quantity counts toward the EHS threshold.'
          : form === 'aqueous_solution'
            ? 'The gas-only EHS listing does not apply to this aqueous solution. Standard hazardous-chemical reporting still applies. Review any separately present hydrogen chloride gas separately.'
            : 'Confirm the hydrogen chloride form using the SDS and site conditions. A liquid storage state alone does not establish an aqueous solution. EHS treatment is provisional until reviewed.',
    };
  }
  const verified = answer && 'concentrationPercent' in answer ? answer.concentrationPercent : null;
  const lower = verified == null ? row.concentrationLower : verified / 100;
  const upper = verified == null ? row.concentrationUpper : verified / 100;
  const status =
    lower != null && lower > 0.52 ? 'applies' : upper != null && upper <= 0.52 ? 'does_not_apply' : 'needs_review';
  return {
    ...rule,
    status,
    review: verified == null || !review ? null : { answer: { concentrationPercent: verified }, note: review.note },
    explanation:
      status === 'applies'
        ? 'The concentration is greater than 52%, so the hydrogen peroxide EHS listing applies.'
        : status === 'does_not_apply'
          ? 'The concentration is 52% or less. The EHS listing does not apply; standard hazardous-chemical reporting still applies.'
          : 'The recorded concentration is missing or spans 52%. Verify the highest concentration present during this reporting year. EHS treatment is provisional until reviewed.',
  };
}
